/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @link      https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments2026.
 * @license   MIT
 * @description Enhanced weapon shop with item type filtering, images, and specific stat displays.
 */

import React, { useState, useEffect } from "react";
import {
  Shield,
  Crosshair,
  Search,
  Filter,
  ArrowUpDown,
  Flame,
  LayoutGrid,
  DollarSign,
  Zap,
  Briefcase,
  Award,
  ShoppingCart,
  CheckSquare,
} from "lucide-react";
import { useNotify } from "./Notification";
import { callBackend } from "../utils/api";
import { log } from "../utils/logger";

const RARITY_THEMES = {
  common: {
    border: "border-zinc-800",
    bg: "bg-zinc-900/50",
    text: "text-zinc-400",
    label: "COMMON",
  },
  rare: {
    border: "border-blue-600",
    bg: "bg-blue-900/10",
    text: "text-blue-400",
    label: "RARE",
    shadow: "shadow-[0_0_10px_rgba(59,130,246,0.2)]",
  },
  epic: {
    border: "border-purple-600",
    bg: "bg-purple-900/10",
    text: "text-purple-400",
    label: "EPIC",
    shadow: "shadow-[0_0_10px_rgba(168,85,247,0.2)]",
  },
  legendary: {
    border: "border-yellow-500",
    bg: "bg-yellow-900/10",
    text: "text-yellow-400",
    label: "LEGENDARY",
    shadow: "shadow-[0_0_15px_rgba(234,179,8,0.3)]",
  },
};

export default function BlackMarket({
  userCash,
  userLevel,
  onPurchaseSuccess,
}) {
  const [weapons, setWeapons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rarityFilter, setRarityFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("price_desc");
  const [affordableOnly, setAffordableOnly] = useState(false);
  const { addNotification } = useNotify();

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const data = await callBackend("/shop/inventory");
        setWeapons(data);
        setLoading(false);
      } catch (err) {
        log("WEAPON_SHOP", "Failed to load armory", err);
      }
    };
    fetchInventory();
  }, []);

  const handleBuy = async (itemId) => {
    try {
      const data = await callBackend("/shop/buy", {
        method: "POST",
        body: { itemId, quantity: 1 },
      });
      addNotification(data.message, "success");
      onPurchaseSuccess();
    } catch (err) {
      addNotification(err.message || "Transaction failed.", "error");
      log("WEAPON_SHOP", "Purchase failed", err);
    }
  };

  // Helper to get image path
  const getItemImage = (slug) => {
    return `/assets/items/${slug || "Default"}.png`;
  };

  // Helper to render stats based on item type
  const renderItemStats = (item) => {
    switch (item.type) {
      case "weapon":
      case "mercenary":
      case "dog":
        return (
          <div className="grid grid-cols-2 gap-2 w-full mt-2">
            <div className="bg-zinc-900/50 p-1 border border-zinc-800 flex flex-col items-center">
              <span className="text-[8px] text-zinc-500 font-bold uppercase flex items-center gap-1">
                <Crosshair size={10} className="text-red-500" /> ATK
              </span>
              <span className="text-md font-black text-white">
                {item.atk_power}
              </span>
            </div>
            <div className="bg-zinc-900/50 p-1 border border-zinc-800 flex flex-col items-center">
              <span className="text-[8px] text-zinc-500 font-bold uppercase flex items-center gap-1">
                <Shield size={10} className="text-blue-500" /> DEF
              </span>
              <span className="text-md font-black text-white">
                {item.def_power}
              </span>
            </div>
          </div>
        );

      case "armor":
        return (
          <div className="w-full mt-2">
            <div className="bg-zinc-900/50 p-1 border border-zinc-800 flex flex-col items-center">
              <span className="text-[8px] text-zinc-500 font-bold uppercase flex items-center gap-1">
                <Shield size={10} className="text-blue-500" /> DEFENSE
              </span>
              <span className="text-lg font-black text-white">
                {item.def_power}
              </span>
            </div>
          </div>
        );

      case "girl":
        return (
          <div className="w-full mt-2">
            <div className="bg-pink-900/20 p-1 border border-pink-900/50 flex flex-col items-center">
              <span className="text-[8px] text-pink-400 font-bold uppercase flex items-center gap-1">
                <DollarSign size={10} /> Passive Income
              </span>
              <span className="text-lg font-black text-pink-200">
                +${(item.passive_income || 0).toLocaleString()}/tick
              </span>
            </div>
          </div>
        );

      case "cook":
        return (
          <div className="grid grid-cols-2 gap-2 w-full mt-2">
            <div className="bg-green-900/20 p-1 border border-green-900/50 flex flex-col items-center">
              <span className="text-[8px] text-green-400 font-bold uppercase flex items-center gap-1">
                <Zap size={10} /> Rate
              </span>
              <span className="text-md font-black text-green-200">
                {item.production_rate || 0} u/tick
              </span>
            </div>
            <div className="bg-zinc-900/50 p-1 border border-zinc-800 flex flex-col items-center">
              <span className="text-[8px] text-zinc-500 font-bold uppercase flex items-center gap-1">
                <Briefcase size={10} /> Skill
              </span>
              <span className="text-md font-black text-white">
                Lvl {item.production_skill || 1}
              </span>
            </div>
          </div>
        );

      case "runner":
        return (
          <div className="w-full mt-2">
            <div className="bg-yellow-900/20 p-1 border border-yellow-900/50 flex flex-col items-center">
              <span className="text-[8px] text-yellow-500 font-bold uppercase flex items-center gap-1">
                <Zap size={10} /> Speed / Capacity
              </span>
              <span className="text-sm font-black text-yellow-200 mt-1">
                Logistics Unit
              </span>
            </div>
          </div>
        );

      case "collectible":
        return (
          <div className="w-full mt-2">
            <div className="bg-zinc-900 p-1 border border-zinc-800 flex flex-col items-center justify-center h-[42px]">
              <span className="text-[9px] text-zinc-500 font-bold uppercase flex items-center gap-1 italic">
                <Award size={10} /> Collectible Only
              </span>
            </div>
          </div>
        );

      default:
        return <div className="h-[42px] w-full mt-2"></div>;
    }
  };

  const filteredWeapons = weapons
    .filter((w) => rarityFilter === "all" || w.rarity === rarityFilter)
    .filter((w) => typeFilter === "all" || w.type === typeFilter)
    .filter((w) => w.name.toLowerCase().includes(search.toLowerCase()))
    .filter((w) => {
      if (!affordableOnly) return true;
      return w.price <= userCash && w.min_level <= userLevel;
    })
    .sort((a, b) => {
      if (sortBy === "price_desc") return b.price - a.price;
      if (sortBy === "price_asc") return a.price - b.price;
      if (sortBy === "atk") return b.atk_power - a.atk_power;
      if (sortBy === "lvl_desc") return b.min_level - a.min_level; // Sort by highest level requirement first
      if (sortBy === "lvl_asc") return a.min_level - b.min_level; // Sort by lowest level requirement first
      return 0;
    });

  if (loading)
    return (
      <div className="text-green-500 font-mono">LOADING BLACK MARKET...</div>
    );

  return (
    <div className="bg-zinc-950 p-8 border-2 border-zinc-900 hover:border-red-900/40 transition-all duration-300 rounded-sm font-mono">
      {/* Header & Controls */}
      <div className="flex justify-between items-center mb-8 border-b border-red-900 pb-4">
        <h2 className="text-4xl font-black text-red-600 uppercase italic">
          The Black Market
        </h2>
        <div className="text-right">
          <p className="text-xs text-zinc-500 uppercase tracking-widest">
            Available Funds
          </p>
          <p className="text-2xl text-green-500 font-bold">
            ${userCash.toLocaleString()}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 mb-6 bg-zinc-900/50 p-4 border border-zinc-800">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-3 text-zinc-600" />
          <input
            className="w-full bg-black border border-zinc-700 p-2 pl-10 text-xs text-white outline-none focus:border-red-600"
            placeholder="SEARCH BLACK MARKET..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setAffordableOnly(!affordableOnly)}
            className={`flex items-center gap-2 border px-3 py-1 text-[10px] font-black uppercase italic transition-all ${
              affordableOnly
                ? "bg-green-600 border-green-500 text-white shadow-[0_0_10px_rgba(22,163,74,0.4)]"
                : "bg-black border-zinc-700 text-zinc-500 hover:text-white"
            }`}
          >
            <CheckSquare size={14} />{" "}
            {affordableOnly ? "SHOWING ELIGIBLE" : "FILTER ELIGIBLE"}
          </button>
          {/* Item Type Filter */}
          <div className="flex items-center gap-2 bg-black border border-zinc-700 px-3 py-1">
            <LayoutGrid size={14} className="text-zinc-500" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="bg-transparent text-[10px] text-white outline-none uppercase cursor-pointer"
            >
              <option value="all" className="bg-black text-white">
                ALL TYPES
              </option>
              <option value="weapon" className="bg-black text-white">
                WEAPONS
              </option>
              <option value="mercenary" className="bg-black text-white">
                THUGS/MERCENARIES
              </option>
              <option value="girl" className="bg-black text-white">
                GIRLS/HOS
              </option>
              <option value="cook" className="bg-black text-white">
                COOKS/CHEMISTRY
              </option>
              <option value="runner" className="bg-black text-white">
                RUNNERS/LOGISTICS
              </option>
              <option value="dog" className="bg-black text-white">
                K9 UNITS
              </option>
              <option value="armor" className="bg-black text-white">
                PROTECTION
              </option>
            </select>
          </div>

          <div className="flex items-center gap-2 bg-black border border-zinc-700 px-3 py-1">
            <Filter size={14} className="text-zinc-500" />
            <select
              value={rarityFilter}
              onChange={(e) => setRarityFilter(e.target.value)}
              className="bg-transparent text-[10px] text-white outline-none uppercase cursor-pointer"
            >
              <option value="all" className="bg-black text-white">
                ALL RARITIES
              </option>
              <option value="common" className="bg-black text-white">
                COMMON
              </option>
              <option value="rare" className="bg-black text-white">
                RARE
              </option>
              <option value="epic" className="bg-black text-white">
                EPIC
              </option>
              <option value="legendary" className="bg-black text-white">
                LEGENDARY
              </option>
            </select>
          </div>

          <div className="flex items-center gap-2 bg-black border border-zinc-700 px-3 py-1">
            <ArrowUpDown size={14} className="text-zinc-500" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-transparent text-[10px] text-white outline-none uppercase cursor-pointer"
            >
              <option value="price_desc" className="bg-black text-white">
                PRICE: HIGH TO LOW
              </option>
              <option value="price_asc" className="bg-black text-white">
                PRICE: LOW TO HIGH
              </option>
              <option value="atk" className="bg-black text-white">
                MOST POWERFUL
              </option>
              <option value="lvl_desc" className="bg-black text-white">
                LEVEL: HIGH TO LOW
              </option>
              <option value="lvl_asc" className="bg-black text-white">
                LEVEL: LOW TO HIGH
              </option>
            </select>
          </div>
        </div>
      </div>

      {/* Item Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredWeapons.map((item) => {
          const theme = RARITY_THEMES[item.rarity || "common"];

          return (
            <div
              key={item.id}
              className={`relative bg-black border-2 transition-all group hover:scale-[1.02] hover:z-10 flex flex-col justify-between ${theme.border} ${theme.shadow}`}
            >
              {/* Header */}
              <div
                className={`p-2 border-b ${theme.border} ${theme.bg} flex justify-between items-center`}
              >
                <span
                  className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 border ${theme.border} ${theme.text} bg-black`}
                >
                  {theme.label}
                </span>
                <span className="text-xs font-bold text-zinc-500 bg-black px-2 py-1 border border-zinc-800">
                  LVL {item.min_level}
                </span>
              </div>

              {/* Body */}
              <div className="p-4 space-y-3 flex-1 flex flex-col items-center">
                {/* Custom Icon Image */}
                <div className="w-full h-24 flex items-center justify-center bg-zinc-900/30 rounded-lg mb-2 relative overflow-hidden group-hover:bg-zinc-900/50 transition-colors">
                  {/* Background Glow based on rarity */}
                  <div
                    className={`absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity ${theme.bg}`}
                  ></div>

                  <img
                    src={getItemImage(item.icon_slug)}
                    alt={item.name}
                    className="w-full h-full object-contain p-2 drop-shadow-[0_5px_5px_rgba(0,0,0,0.5)] transform group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/assets/items/Default.png";
                    }}
                  />
                </div>

                <div className="text-center w-full">
                  <h4 className="text-sm font-black text-white uppercase italic leading-tight truncate">
                    {item.name}
                  </h4>
                  {item.rarity === "legendary" && (
                    <div className="flex justify-center mt-1">
                      <Flame
                        size={12}
                        className="text-yellow-500 animate-pulse"
                      />
                    </div>
                  )}
                </div>

                {/* Specific Stats Rendering */}
                {renderItemStats(item)}
              </div>

              {/* Action Footer */}
              <div className="p-3 bg-zinc-950 border-t border-zinc-900">
                <button
                  onClick={() => handleBuy(item.id)}
                  className="w-full py-2 bg-zinc-800 hover:bg-green-600 hover:text-white text-zinc-400 font-black transition-all uppercase italic text-[10px] tracking-wider border border-zinc-800 hover:border-green-500 flex items-center justify-center gap-2"
                >
                  <ShoppingCart size={14} /> Buy ${item.price.toLocaleString()}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
